import { API } from '../Api';
import { getClanPosts } from '../posts/clan-posts.api';

// Arena (đấu trường theo đội). Đi qua group-service, response bọc { httpCode, message, data }.
// Gateway tự gắn auid nên không gửi từ web.
const BASE = '/v1/group-service/arena';

export type ArenaStatus = 'DRAFT' | 'PUBLISHED' | 'JUDGING' | 'FINALIZED' | 'CANCELLED';

export type ArenaChallengeType = 'NORMAL' | 'CLAN_WAR';

export interface ArenaChallenge {
  id: string;
  title: string;
  description: string;
  rules: string;
  startAt: string;
  submitDeadline: string;
  judgingDeadline: string;
  teamSizeMin: number;
  teamSizeMax: number;
  interactionWeight: number;
  pointsRank1: number;
  pointsRank2: number;
  pointsRank3: number;
  pointsRank4To10: number;
  pointsParticipation: number;
  type?: ArenaChallengeType | string;
  categoryId?: string | null;
  status: ArenaStatus | string;
  finalizedAt?: string | null;
  quarter?: string | null;
  isOpen: boolean;
}

export interface ArenaChallengeListItem extends ArenaChallenge {
  myTeamId?: string | null;
  myTeamName?: string | null;
  myTeamHasEntry: boolean;
}

export interface ArenaTeamMember {
  userId: string;
  clanIdSnapshot?: string | null;
  joinedAt: string;
  isLeader: boolean;
}

export interface ArenaEntry {
  postId: string;
  postAuthorId: string;
  submittedAt: string;
  disqualified: boolean;
  disqualifyReason?: string | null;
}

export interface ArenaTeam {
  id: string;
  name: string;
  leaderUserId: string;
  inviteCode?: string | null;
  status: string;
  members: ArenaTeamMember[];
  entry?: ArenaEntry | null;
}

export interface ArenaLeaderboardRow {
  teamId: string;
  teamName: string;
  rank?: number | null;
  finalScore?: number | null;
  points: number;
  postId: string;
  disqualified: boolean;
  disqualifyReason?: string | null;
}

export interface ArenaClanLeaderboardRow {
  clanId: string;
  clanName: string;
  logo?: string | null;
  crestColor?: string | null;
  rank?: number | null;
  totalFinalScore: number;
  points: number;
  teamCount: number;
}

export interface ArenaChallengeDetail {
  challenge: ArenaChallenge;
  myTeam?: ArenaTeam | null;
  leaderboard?: ArenaLeaderboardRow[] | null;
  clanLeaderboard?: ArenaClanLeaderboardRow[] | null;
}

export interface ArenaMemberProfile {
  fullName: string;
  avatar: string;
}

export interface ArenaPostOption {
  id: string;
  title: string;
  authorName: string;
  createdAt: string;
}

interface ResultResponse<T> {
  httpCode: number;
  message: string;
  data: T;
}

// Gỡ bọc ResultResponse: httpCode khác 200 thì ném message để UI hiện toast.
export const unwrap = <T>(r: { data: ResultResponse<T> }): T => {
  const body = r.data;
  if (!body || body.httpCode !== 200) {
    throw new Error(body?.message || 'Có lỗi xảy ra');
  }
  return body.data;
};

export const listChallenges = () =>
  API.get<ResultResponse<ArenaChallengeListItem[]>>(`${BASE}/challenges`).then(unwrap);

export const getChallenge = (id: string) =>
  API.get<ResultResponse<ArenaChallengeDetail>>(`${BASE}/challenge`, { params: { id } }).then(unwrap);

export const createTeam = (challengeId: string, name: string) =>
  API.post<ResultResponse<ArenaTeam>>(`${BASE}/team/create`, { challengeId, name }).then(unwrap);

export const joinTeam = (challengeId: string, inviteCode: string) =>
  API.post<ResultResponse<ArenaTeam>>(`${BASE}/team/join`, { challengeId, inviteCode }).then(unwrap);

export const leaveTeam = (teamId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/team/leave`, { teamId }).then(unwrap);

export const kickMember = (teamId: string, userId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/team/kick`, { teamId, userId }).then(unwrap);

export const submitEntry = (teamId: string, postId: string) =>
  API.post<ResultResponse<ArenaEntry>>(`${BASE}/team/submit`, { teamId, postId }).then(unwrap);

// DTO đội chỉ có userId nên tên và ảnh thành viên lấy qua hồ sơ người dùng (user-service).
export const fetchMemberProfile = async (userId: string): Promise<ArenaMemberProfile> => {
  const res = await API.get(`v1/user-service/users/${userId}`);
  const d = res.data?.data ?? {};
  return { fullName: d.fullname || d.fullName || '', avatar: d.avatar || '' };
};

// Bài của các thành viên đăng từ giờ bắt đầu thử thách, dùng chung endpoint feed theo danh sách người dùng của bang hội.
export const fetchTeamPosts = async (userIds: string[], startAt: string): Promise<ArenaPostOption[]> => {
  const res = await getClanPosts(userIds, 1, 100);
  const from = new Date(startAt).getTime();
  return (res.posts || [])
    .filter((p) => new Date(p.createdAt).getTime() >= from)
    .map((p) => ({
      id: p.id,
      title: p.title || (p.content || '').slice(0, 80) || 'Bài viết',
      authorName: p.userFullName || p.username || '',
      createdAt: p.createdAt,
    }));
};

export interface ArenaClanQuarterRow {
  clanId: string;
  name: string;
  logo?: string | null;
  crestColor?: string | null;
  points: number;
  rank1Count: number;
  rank: number;
  level?: number;
}

export interface ArenaClanBadge {
  clanId: string;
  quarter: string;
  rank: number;
  title: string;
  awardedAt: string;
}

export interface ArenaTrophy {
  challengeId: string;
  teamId: string;
  rank: number;
  title: string;
  awardedAt: string;
}

export const getClanQuarterLeaderboard = (quarter: string) =>
  API.get<ResultResponse<ArenaClanQuarterRow[]>>(`${BASE}/clan-leaderboard`, { params: { quarter } }).then(unwrap);

export const getArenaQuarters = () => API.get<ResultResponse<string[]>>(`${BASE}/quarters`).then(unwrap);

export const getClanBadges = (clanId: string) =>
  API.get<ResultResponse<ArenaClanBadge[]>>(`${BASE}/clan-badges`, { params: { clanId } }).then(unwrap);

export const getUserTrophies = (userId: string) =>
  API.get<ResultResponse<ArenaTrophy[]>>(`${BASE}/trophies`, { params: { userId } }).then(unwrap);

export interface ArenaCategoryHold {
  categoryId: string;
  clanId: string;
  clanName: string;
  weekStart: string;
  points: number;
  pinnedPostId?: string | null;
  pinnedAt?: string | null;
}

export const getClanCategoryHolds = (clanId: string) =>
  API.get<ResultResponse<ArenaCategoryHold[]>>(`${BASE}/category-holds`, { params: { clanId } }).then(unwrap);

export const pinCategoryPost = (categoryId: string, postId: string) =>
  API.post<ResultResponse<ArenaCategoryHold>>(`${BASE}/category-pin`, { categoryId, postId }).then(unwrap);
